import { IntlTFunc } from 'next-intl';

import {
  SimpleProductFragment,
  AttributeSelectedColorfulOption
} from '@/fo/api/magento';

import { CharacteristicOption, Characteristics, Expert } from '../types';

type MapAttribute = {
  [key: string]: {
    selected_options: AttributeSelectedColorfulOption[];
    value: string;
  };
};

export const attributesMapper = (
  attributesRaw: SimpleProductFragment['attributes'],
  t: IntlTFunc
) => {
  const items = attributesRaw?.items ?? [];

  const map: MapAttribute = Object.fromEntries(
    items
      .map((item) => [item && 'code' in item ? item.code : '', item])
      .filter(([code]) => code)
  );

  const getSelectedOptions = (code: string): CharacteristicOption[] =>
    map[code]?.selected_options
      ?.map((option) => ({
        label: option?.label.trim() ?? '',
        url: option?.url ?? undefined
      }))
      .filter((option) => option.label) || [];

  const getSelectedLabels = (code: string) =>
    getSelectedOptions(code)
      .map((option) => option.label)
      .join(', ');

  const getValue = (code: string) => map[code]?.value || '';
  const getOption = (code: string) => map[code]?.selected_options?.[0];
  const getOptionLabel = (code: string) => getOption(code)?.label.trim() || '';
  const getOptionByPrefix = (prefix: string) => {
    const key = Object.keys(map).find((attributeCode) =>
      attributeCode.startsWith(prefix)
    );
    return key ? map[key]?.selected_options?.[0] : undefined;
  };

  const volume = getOption('volume') || null;
  const video = getValue('gallery_video') || null;
  const labels = map['label']?.selected_options;

  const expert: Expert = {
    name: getValue('export_opinion_name') || null,
    job: getValue('export_opinion_post') || null,
    text: getValue('export_opinion_text') || null,
    quote: getValue('export_opinion_quote') || null
  };

  const characteristics: Characteristics = [
    {
      label: t('product.characteristics.age'),
      value: getSelectedLabels('age'),
      options: getSelectedOptions('age')
    },
    {
      label: t('product.characteristics.gender'),
      value: getSelectedLabels('pol'),
      options: getSelectedOptions('pol')
    },
    {
      label: t('product.characteristics.problems'),
      value: getSelectedLabels('problems'),
      options: getSelectedOptions('problems')
    },
    {
      label: t('product.characteristics.skinType'),
      value: getSelectedLabels('skin_type'),
      options: getSelectedOptions('skin_type')
    },
    {
      label: t('product.characteristics.texture'),
      value: getOptionLabel('texture'),
      options: getSelectedOptions('texture')
    },
    {
      label: t('product.characteristics.applicationArea'),
      value: getSelectedLabels('application_area'),
      options: getSelectedOptions('application_area')
    },
    {
      label: t('product.characteristics.timeOfUse'),
      value: getSelectedLabels('vremya_ispolzovaniya'),
      options: getSelectedOptions('vremya_ispolzovaniya')
    },
    {
      label: t('product.characteristics.effect'),
      value: getSelectedLabels('effect'),
      options: getSelectedOptions('effect')
    },
    {
      label: t('product.characteristics.fragrance'),
      value: getOptionLabel('otdushka'),
      options: getSelectedOptions('otdushka')
    },
    {
      label: t('product.characteristics.brand'),
      value: getOptionLabel('brand'),
      url: getOption('brand')?.url ?? undefined,
      options: getSelectedOptions('brand')
    },
    {
      label: t('product.characteristics.gamma'),
      value:
        getOptionByPrefix('gamma_')?.label.trim() || getOptionLabel('gamma'),
      url:
        getOptionByPrefix('gamma_')?.url ??
        getOption('gamma')?.url ??
        undefined,
      options: getSelectedOptions('gamma')
    },
    {
      label: t('product.characteristics.country'),
      value: t('madeFromCountry')
    },
    {
      label: t('product.characteristics.volume'),
      value: volume?.label.trim() ?? '',
      options: getSelectedOptions('volume')
    },
    {
      label: t('product.characteristics.spf'),
      value: getOptionLabel('sun_protection_factor'),
      options: getSelectedOptions('sun_protection_factor')
    }
  ].filter((item) => item.value);

  const gammas = (() => {
    const prefixed = Object.keys(map)
      .filter((key) => key.startsWith('gamma_'))
      .map(getOption)
      .filter((option) => option?.value);
    if (prefixed.length > 0) return prefixed;
    const gamma = getOption('gamma');
    return gamma?.value ? [gamma] : [];
  })();

  const diagnosticLabels =
    map['diagnostic_label']?.selected_options
      ?.map((option) => option?.label.trim())
      .filter(Boolean) || [];

  return {
    brand: getOption('brand')?.value ? getOption('brand') : null,
    gammas,

    productGroup: getOption('product_group')?.value
      ? getOption('product_group')
      : null,

    name: getValue('name'),
    subtitle: getValue('product_name_subtitle'),
    shortDescription: getValue('short_description'),
    usageInstructions: getValue('usage_instructions'),
    activeComponents: getValue('active_components'),
    volume,
    effects: getSelectedLabels('effect'),
    benefits: getValue('benefits'),
    characteristics,
    purpose: getValue('purpose'),
    labels,
    video,
    expert,
    structure_image: getValue('structure_image'),
    visibility: getSelectedLabels('visibility'),
    mindboxId: getValue('mindbox_id'),
    brandCode: getOption('brand')?.value,
    diagnosticLabels,
    videoExpertOpinionPreview: getValue('video_expert_opinion_preview'),
    videoExpertOpinionVideo: getValue('video_expert_opinion_video'),
    videoExpertOpinionExpertUrlKey: getValue(
      'video_expert_opinion_expert_url_key'
    )
  };
};
